import React from 'react';
import { Clock, Calendar } from 'lucide-react';

const BusinessHours = ({ openingHours, t, className = '' }) => {
  // Helper function to parse working hours from various formats
  const parseWorkingHours = (hoursText) => {
    if (!hoursText) return null;

    const daysMap = {
      // English
      'monday': 'Monday', 'mon': 'Monday',
      'tuesday': 'Tuesday', 'tue': 'Tuesday',
      'wednesday': 'Wednesday', 'wed': 'Wednesday',
      'thursday': 'Thursday', 'thu': 'Thursday',
      'friday': 'Friday', 'fri': 'Friday',
      'saturday': 'Saturday', 'sat': 'Saturday',
      'sunday': 'Sunday', 'sun': 'Sunday',
      // French
      'lundi': 'Monday', 'lun': 'Monday',
      'mardi': 'Tuesday', 'mar': 'Tuesday', 
      'mercredi': 'Wednesday', 'mer': 'Wednesday',
      'jeudi': 'Thursday', 'jeu': 'Thursday',
      'vendredi': 'Friday', 'ven': 'Friday',
      'samedi': 'Saturday', 'sam': 'Saturday',
      'dimanche': 'Sunday', 'dim': 'Sunday',
      // Spanish
      'lunes': 'Monday',
      'martes': 'Tuesday',
      'miércoles': 'Wednesday', 'miercoles': 'Wednesday',
      'jueves': 'Thursday', 
      'viernes': 'Friday',
      'sábado': 'Saturday', 'sabado': 'Saturday',
      'domingo': 'Sunday',
      // Arabic
      'الاثنين': 'Monday',
      'الثلاثاء': 'Tuesday',
      'الأربعاء': 'Wednesday',
      'الخميس': 'Thursday',
      'الجمعة': 'Friday',
      'السبت': 'Saturday',
      'الأحد': 'Sunday'
    };

    const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const parsedHours = {};

    // Split by lines first
    const lines = hoursText.split('\n').map(line => line.trim()).filter(line => line);

    lines.forEach(line => {
      // Try different patterns
      
      // Pattern 1: "Monday - Friday: 7:00 AM - 7:00 PM"
      const rangePattern = /^(.+?)\s*[-–]\s*(.+?)\s*:\s*(.+)$/i;
      const rangeMatch = line.match(rangePattern);
      
      if (rangeMatch) {
        const startDay = rangeMatch[1].toLowerCase().trim();
        const endDay = rangeMatch[2].toLowerCase().trim();
        const hours = rangeMatch[3].trim();
        
        const startDayName = daysMap[startDay];
        const endDayName = daysMap[endDay];
        
        if (startDayName && endDayName) {
          const startIndex = dayOrder.indexOf(startDayName);
          const endIndex = dayOrder.indexOf(endDayName);
          
          for (let i = startIndex; i <= endIndex; i++) {
            parsedHours[dayOrder[i]] = hours;
          }
          return;
        }
      }

      // Pattern 2: "Monday: 7:00 AM - 7:00 PM"
      const singleDayPattern = /^(.+?)\s*:\s*(.+)$/;
      const singleMatch = line.match(singleDayPattern);
      
      if (singleMatch) {
        const day = singleMatch[1].toLowerCase().trim();
        const hours = singleMatch[2].trim();
        const dayName = daysMap[day];
        
        if (dayName) {
          parsedHours[dayName] = hours;
        }
      }
    });

    return parsedHours;
  };

  // Get current status
  const getCurrentStatus = (parsedHours) => {
    if (!parsedHours) return null;

    const now = new Date();
    const currentDay = now.toLocaleDateString('en-US', { weekday: 'long' });
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight

    const todayHours = parsedHours[currentDay];
    if (!todayHours) return { status: 'closed', text: t ? t('closed') : 'Closed' };

    // Check if closed
    if (todayHours.toLowerCase().includes('closed') || todayHours.toLowerCase().includes('close')) {
      return { status: 'closed', text: t ? t('closed') : 'Closed' };
    }

    // Try to parse time ranges
    const timePattern = /(\d{1,2}):?(\d{0,2})\s*(am|pm)?/gi;
    const times = [];
    let match;
    
    while ((match = timePattern.exec(todayHours)) !== null) {
      let hours = parseInt(match[1]);
      const minutes = match[2] ? parseInt(match[2]) : 0;
      const ampm = match[3] ? match[3].toLowerCase() : '';
      
      if (ampm === 'pm' && hours !== 12) hours += 12;
      if (ampm === 'am' && hours === 12) hours = 0;
      
      times.push(hours * 60 + minutes);
    }

    if (times.length >= 2) {
      const [openTime, closeTime] = times;
      
      if (currentTime >= openTime && currentTime < closeTime) {
        // Check if closing soon (within 30 minutes)
        if (closeTime - currentTime <= 30) {
          return { status: 'closing-soon', text: t ? t('closingSoon') : 'Closing Soon' };
        }
        return { status: 'open', text: t ? t('open') : 'Open' };
      } else if (currentTime < openTime) {
        // Check if opening soon (within 30 minutes)
        if (openTime - currentTime <= 30) {
          return { status: 'opening-soon', text: t ? t('openingSoon') : 'Opening Soon' };
        }
        return { status: 'closed', text: t ? t('closed') : 'Closed' };
      } else {
        return { status: 'closed', text: t ? t('closed') : 'Closed' };
      }
    }

    return { status: 'unknown', text: t ? t('checkHours') : 'Check Hours' };
  };

  const parsedHours = parseWorkingHours(openingHours);
  const currentStatus = getCurrentStatus(parsedHours);
  
  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  // Get translated day names
  const getTranslatedDay = (day) => {
    if (!t) return day;
    const dayKey = day.toLowerCase();
    return t(dayKey) || day;
  };

  // If we couldn't parse the hours, show the original format
  if (!parsedHours || Object.keys(parsedHours).length === 0) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-[#a15507]" />
          <h3 className="text-xl font-semibold text-white">
            {t ? t('businessHours') : 'Business Hours'}
          </h3>
        </div>
        <div className="text-gray-300 whitespace-pre-line text-sm leading-relaxed bg-gray-800/30 p-4 rounded-lg border border-gray-600/30">
          {openingHours}
        </div>
      </div>
    );
  }

  return (
    <div className={`p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#a15507]" />
          <h3 className="text-xl font-semibold text-white">
            {t ? t('businessHours') : 'Business Hours'}
          </h3>
        </div>
        {currentStatus && (
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            currentStatus.status === 'open' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
            currentStatus.status === 'closed' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
            currentStatus.status === 'closing-soon' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' :
            currentStatus.status === 'opening-soon' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
            'bg-gray-500/20 text-gray-400 border border-gray-500/30'
          }`}>
            {currentStatus.text}
          </div>
        )}
      </div>

      <div className="bg-gray-800/30 p-4 rounded-lg border border-gray-600/30">
        <div className="space-y-2">
          {dayOrder.map(day => {
            const hours = parsedHours[day];
            const isToday = new Date().toLocaleDateString('en-US', { weekday: 'long' }) === day;
            
            return (
              <div 
                key={day}
                className={`flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
                  isToday ? 'bg-[#a15507]/10 border border-[#a15507]/20' : 'hover:bg-gray-700/30'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isToday && <Calendar className="w-4 h-4 text-[#a15507]" />}
                  <span className={`text-sm font-medium ${isToday ? 'text-[#a15507]' : 'text-gray-300'}`}>
                    {getTranslatedDay(day)}
                  </span>
                </div>
                <span className={`text-sm ${isToday ? 'text-white font-medium' : 'text-gray-400'}`}>
                  {hours || (t ? t('closed') : 'Closed')}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BusinessHours;
